import React, { useState, useEffect } from 'react';
import ItemCalendar from '../ItemCalendar';
import service from 'Api/service';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as calendarsActions from 'Actions/calendarsActionCreators';
import { Product } from '../../../types/Product';
import { ROOT_URL } from 'Api';
import moment from 'moment-timezone';
import { computeDailyAvailabilityFlags } from 'HelperFunctions/inventoryAvailability';
import { DailyAvailabilityDay } from '../../../types/DailyAvailability';

interface ItemCalendarContainerProps {
  highlight: any;
  product: Product;
  productType: string;
  currentDate?: Date;
  endDate?: Date;
  onDateChange?: (property: string, value: any) => void;
}

/**
 *
 * This component can be controlled (through currentDate, endDate, and onDateChange props) or uncontrolled.
 * When uncontrolled, it'll manage the start/end dates internally through state.
 *
 * The reason is because currentDate is not always provided, but is needed to fetch daily availability.
 * Still, most places using this component don't need to interact or access these dates, so having it
 *   uncontrolled is more convenient.
 */
const ItemCalendarContainer: React.FC<ItemCalendarContainerProps> = ({
  highlight,
  product,
  productType,
  currentDate,
  endDate,
  onDateChange,
}: ItemCalendarContainerProps) => {
  const [datesArray, setDatesArray] = useState<any[]>([]);
  const [alertingDatesArray, setAlertingDatesArray] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selfStartDate, setSelfStartDate] = useState<Date>(
    () => currentDate ?? new Date()
  );
  const [selfEndDate, setSelfEndDate] = useState<Date>(
    () => endDate ?? new Date()
  );

  const viewYear = selfStartDate.getFullYear();
  const viewMonth = selfStartDate.getMonth();

  useEffect(() => {
    if (currentDate == null) {
      return;
    }

    setSelfStartDate((prev) =>
      prev.getTime() === currentDate.getTime() ? prev : currentDate
    );
  }, [currentDate]);

  useEffect(() => {
    if (endDate == null) {
      return;
    }

    setSelfEndDate((prev) =>
      prev.getTime() === endDate.getTime() ? prev : endDate
    );
  }, [endDate]);

  useEffect(() => {
    const monthStartDate = new Date(viewYear, viewMonth, 1);
    const monthEndDate = new Date(viewYear, viewMonth + 1, 0);

    fetchDailyAvailability(monthStartDate, monthEndDate);
  }, [viewYear, viewMonth, product.id, productType]);

  const handleChangeMonth = (month: number, year: number) => {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    if (onDateChange) {
      onDateChange('startDate', startDate);
      onDateChange('endDate', endDate);
    } else {
      setSelfStartDate(startDate);
      setSelfEndDate(endDate);
    }
  };

  const fetchDailyAvailability = (
    startDate = new Date(),
    endDate = new Date()
  ) => {
    const { id } = product;

    const inventoryKeyMapper: any = {
      items: 'products',
      addons: 'add_ons',
      bundles: 'bundles',
      accessories: 'accessories',
    };

    const requestStartDate = new Date(startDate);
    const requestEndDate = new Date(endDate);

    requestStartDate.setHours(0, 0, 0);
    requestEndDate.setHours(23, 59, 59);

    const timeZone = moment.tz.guess();

    setLoading(true);

    service
      .post(`${ROOT_URL}/inventories/daily_availability`, {
        start_date: requestStartDate,
        end_date: requestEndDate,
        rental_statuses: ['reservation', 'in_use', 'checked_in'],
        [inventoryKeyMapper[productType]]: [id],
        time_zone: timeZone,
      })
      .then((res: any) => {
        let responseInventoryKey = inventoryKeyMapper[productType];

        if (responseInventoryKey === 'add_ons') {
          responseInventoryKey = 'addOns';
        }

        const bookedDates: any[] = [];
        const overbookedDates: any[] = [];

        Object.entries(
          res.data.availability[responseInventoryKey][id] as Record<
            string,
            DailyAvailabilityDay
          >
        ).forEach(([dateTimestamp, availabilityData]) => {
            const { isRentedInPeriod, overbooked } = computeDailyAvailabilityFlags(
              availabilityData,
              dateTimestamp,
              responseInventoryKey
            );

            function getStartAndEndOfDay(date: string) {
              const start = moment
                .tz(date, 'YYYY-MM-DD', timeZone)
                .startOf('day')
                .toDate();
              const end = moment
                .tz(date, 'YYYY-MM-DD', timeZone)
                .endOf('day')
                .toDate();

              return [start, end];
            }

            if (isRentedInPeriod)
              bookedDates.push(getStartAndEndOfDay(dateTimestamp));

            if (overbooked)
              overbookedDates.push(getStartAndEndOfDay(dateTimestamp));
        });

        setLoading(false);

        setDatesArray(bookedDates);
        setAlertingDatesArray(overbookedDates);
      });
  };

  return (
    <ItemCalendar
      highlight={highlight}
      datesArray={datesArray}
      productType={productType}
      alertingDatesArray={alertingDatesArray}
      product={product}
      loading={loading}
      numberOfMonths={1}
      currentDate={selfStartDate}
      endDate={selfEndDate}
      onChangeMonthCallback={handleChangeMonth}
    />
  );
};

export default connect(
  (state) => ({
    selectedDate: state.calendars.selectedDate,
  }),
  function(dispatch) {
    return {
      calendarsActions: bindActionCreators(calendarsActions, dispatch),
    };
  }
)(ItemCalendarContainer);
